/**
 * SupportPanel
 * Tela de entrada da Central de Atendimento: números do dia, avaliações do time e o
 * cadastro de quem atende — antes de entrar de fato nas conversas (modelo 1 aprovado).
 */
import React, { useCallback, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, Forum } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAcao } from '../../authorization/useVeredito';
import { userService } from '../../services/userService';
import type { SupportFeedbackTag, SupportStats } from '../../services/supportChatService';
import { AddSupportMemberDialog } from './AddSupportMemberDialog';

const FEEDBACK_LABEL: Record<SupportFeedbackTag, string> = {
  resolveu: 'Resolveu o problema',
  rapido: 'Atendimento rápido',
  educado: 'Atendente educado',
  demorou: 'Demorou muito',
  nao_resolveu: 'Não resolveu',
};

const FEEDBACK_ORDER: SupportFeedbackTag[] = ['resolveu', 'rapido', 'educado', 'demorou', 'nao_resolveu'];
const NEGATIVE_TAGS = new Set<SupportFeedbackTag>(['demorou', 'nao_resolveu']);

const AVATAR_COLORS = ['primary.main', 'secondary.main', 'info.main', 'success.main', 'warning.main'];

function corDoAvatar(index: number): string {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '?';
  const ultima = partes.length > 1 ? partes[partes.length - 1]?.[0] : '';
  return (primeira + ultima).toUpperCase();
}

interface SupportPanelProps {
  stats: SupportStats | null;
  isLoading: boolean;
  onOpenConversations: () => void;
  onReload: () => void;
}

export const SupportPanel: React.FC<SupportPanelProps> = ({ stats, isLoading, onOpenConversations, onReload }) => {
  const { enqueueSnackbar } = useSnackbar();
  const gerenciarTime = useAcao('user.update', 'Gerenciar time de suporte');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: number; name: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [filtroMembroId, setFiltroMembroId] = useState<'todos' | number>('todos');

  const handleRemoveConfirm = useCallback(async () => {
    if (!memberToRemove) return;
    setIsRemoving(true);
    try {
      await userService.removeSupportResponsible(memberToRemove.id);
      enqueueSnackbar(`${memberToRemove.name} saiu do time de suporte.`, { variant: 'success' });
      setMemberToRemove(null);
      onReload();
    } catch (err) {
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao remover do time de suporte';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsRemoving(false);
    }
  }, [memberToRemove, enqueueSnackbar, onReload]);

  if (isLoading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!stats) return null;

  const membroFiltrado = filtroMembroId === 'todos' ? null : stats.team.find((m) => m.id === filtroMembroId) ?? null;
  // Se a pessoa selecionada saiu do time (removida agora ou desde a última carga), volta a
  // mostrar o time todo em vez de segurar um valor que não existe mais na lista do seletor.
  const valorDoSeletor = membroFiltrado ? filtroMembroId : 'todos';
  const avaliacoesExibidas = membroFiltrado
    ? { ratedCount: membroFiltrado.ratedCount, feedbackPercentages: membroFiltrado.feedbackPercentages }
    : { ratedCount: stats.ratedCount, feedbackPercentages: stats.feedbackPercentages };

  return (
    <Stack spacing={2.5}>
      {/* KPIs */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(auto-fit, minmax(150px, 1fr))' },
          gap: 2,
        }}
      >
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Conversas abertas
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {stats.openCount}
          </Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Na fila
          </Typography>
          <Typography variant="h5" fontWeight={800} color={stats.queueCount > 0 ? 'warning.main' : 'text.primary'}>
            {stats.queueCount}
          </Typography>
          {stats.longWaitCount > 0 && (
            <Typography variant="caption" color="error.main" fontWeight={600}>
              {stats.longWaitCount} esperando muito
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Aguardando resposta
          </Typography>
          <Typography variant="h5" fontWeight={800} color={stats.awaitingCount > 0 ? 'warning.main' : 'text.primary'}>
            {stats.awaitingCount}
          </Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Tempo médio até aceitar
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {stats.avgWaitMinutes !== null ? `${stats.avgWaitMinutes} min` : '—'}
          </Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Tempo médio até responder
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {stats.avgResponseMinutes !== null ? `${stats.avgResponseMinutes} min` : '—'}
          </Typography>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Resolveu o problema
          </Typography>
          <Typography variant="h5" fontWeight={800} color="success.main">
            {stats.feedbackPercentages.resolveu}%
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' }, gap: 2 }}>
        {/* Avaliações */}
        <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Avaliações dos atendentes
            </Typography>
            {stats.team.length > 0 && (
              <TextField
                select
                size="small"
                value={valorDoSeletor}
                onChange={(e) => setFiltroMembroId(e.target.value === 'todos' ? 'todos' : Number(e.target.value))}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="todos">Todo o time</MenuItem>
                {stats.team.map((membro) => (
                  <MenuItem key={membro.id} value={membro.id}>
                    {membro.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          {avaliacoesExibidas.ratedCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {membroFiltrado
                ? `${membroFiltrado.name} ainda não tem conversas avaliadas.`
                : 'Ainda não há conversas avaliadas pelos usuários.'}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {FEEDBACK_ORDER.map((tag) => (
                <Box key={tag}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {FEEDBACK_LABEL[tag]}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {avaliacoesExibidas.feedbackPercentages[tag]}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={avaliacoesExibidas.feedbackPercentages[tag]}
                    color={NEGATIVE_TAGS.has(tag) ? 'error' : 'primary'}
                    sx={{ height: 7, borderRadius: 999 }}
                  />
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary">
                Com base em {avaliacoesExibidas.ratedCount} conversa(s) avaliada(s)
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Time de suporte */}
        <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Time de suporte
            </Typography>
            <Tooltip title={gerenciarTime.tooltip}>
              <span>
                <Button
                  size="small"
                  startIcon={<Add fontSize="small" />}
                  disabled={gerenciarTime.disabled}
                  onClick={() => setAddDialogOpen(true)}
                >
                  Adicionar
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {stats.team.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Ninguém está no time de suporte ainda.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {stats.team.map((membro, index) => (
                <Stack
                  key={membro.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}
                >
                  <Avatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: corDoAvatar(index) }}>
                    {iniciais(membro.name)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {membro.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {membro.activeCount > 0
                        ? `${membro.activeCount} ativa(s) agora · ${membro.repliedCount} no total`
                        : `${membro.repliedCount} atendimento(s)`}
                    </Typography>
                  </Box>
                  {membro.positivePercent !== null ? (
                    <Chip
                      size="small"
                      label={`${membro.positivePercent}%`}
                      color={membro.positivePercent >= 70 ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Sem avaliação
                    </Typography>
                  )}
                  <Tooltip title={gerenciarTime.disabled ? gerenciarTime.tooltip : 'Remover do time'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={gerenciarTime.disabled}
                        onClick={() => setMemberToRemove({ id: membro.id, name: membro.name })}
                        aria-label={`Remover ${membro.name} do time de suporte`}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* CTA */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {stats.awaitingCount > 0
              ? `${stats.awaitingCount} conversa(s) aguardando resposta`
              : 'Nenhuma conversa aguardando resposta agora'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {stats.openCount} conversa(s) aberta(s) no total
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<Forum />}
          onClick={onOpenConversations}
        >
          Abrir conversas
        </Button>
      </Stack>

      <AddSupportMemberDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={onReload}
      />

      <Dialog open={Boolean(memberToRemove)} onClose={() => setMemberToRemove(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remover do time de suporte</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover <strong>{memberToRemove?.name}</strong> do time de
            suporte? A pessoa deixa de ver e responder a Central de Atendimento.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberToRemove(null)}>Cancelar</Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained" disabled={isRemoving}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SupportPanel;
